"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

import { routes } from "@lattice/frontend/lib/routes/routes";

type T_StatsFollowupButtonsProps = {
  symbol: string;
};

export const StatsFollowupButtons: React.FC<T_StatsFollowupButtonsProps> = ({
  symbol,
}) => {
  const router = useRouter();
  const [isMounted, setIsMounted] = React.useState(false);

  // Ensure component is mounted before showing dynamic content
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  const { data, isLoading, error } =
    trpcApi.stock.get_stats_followup_questions.useQuery(
      { symbol },
      {
        // Enable aggressive caching to avoid regenerating questions
        staleTime: 1000 * 60 * 60, // 1 hour
        gcTime: 1000 * 60 * 60 * 24, // 24 hours (formerly cacheTime)
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
    );

  const handleQuestionClick = React.useCallback(
    (question: string) => {
      router.push(routes.shellPrompt(question));
    },
    [router],
  );

  // Don't render until mounted to avoid hydration issues
  if (!isMounted) {
    return null;
  }

  // Loading state with shimmer animation
  if (isLoading) {
    return (
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <h4 className="text-sm text-muted-foreground/70">Keep Exploring</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="relative p-5 bg-gradient-to-br from-primary/12 to-primary/6 border border-primary/30 rounded-xl overflow-hidden"
            >
              <div className="absolute inset-0 -translate-x-full animate-[shimmer_3s_infinite]">
                <div className="h-full w-full bg-gradient-to-r from-transparent via-white/[0.03] to-transparent" />
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary/80 animate-pulse mt-1.5 flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  <div className="h-3 bg-primary/20 rounded w-full" />
                  <div className="h-3 bg-primary/20 rounded w-3/4" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state - gracefully degrade (don't show anything if error)
  if (error) {
    console.error("Error loading stats followup questions:", error);
    return null;
  }

  // No questions available or API returned error
  if (!data?.questions || data.questions.length === 0 || data.error) {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        className="mt-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <h4 className="text-sm text-muted-foreground/70">Keep Exploring</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {data.questions.slice(0, 4).map((question, index) => (
            <motion.button
              key={index}
              onClick={() => handleQuestionClick(question)}
              className="relative text-left p-5 bg-gradient-to-br from-primary/12 to-primary/6 hover:from-primary/17 hover:to-primary/10 border border-primary/30 hover:border-primary/45 rounded-xl transition-all duration-300 focus:outline-none group overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/0 group-hover:from-primary/5 group-hover:to-transparent transition-all duration-300" />

              {/* Content */}
              <div className="relative flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary/80 group-hover:bg-primary/95 animate-pulse mt-1.5 flex-shrink-0" />
                <p className="text-sm text-foreground/80 group-hover:text-foreground/90 transition-colors leading-relaxed line-clamp-3">
                  {question}
                </p>
              </div>

              {/* Subtle corner accent */}
              <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.button>
          ))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
